// src/pages/Team/FloorBoard.jsx
// STATUS: active | UPDATED: 2026-09-24
// LOG:
//   2026-09-24  New. Tyler asked for a leaderboard. This is the version that
//               scores the thing Jazz actually asks to be recognised.
//
// WHO IS PUTTING THE WORK IN, THIS WEEK.
//
// Every dealership already has a leaderboard and it ranks units. Jazz says
// twice in his own material not to do that: "Recognition of process behaviors,
// not only deliveries."
//
// Nobody scores process because process is invisible. This product can see it.
//
// THREE THINGS ABOUT THE DESIGN THAT ARE NOT DECORATION.
//
// It has no last place. Anybody who did nothing this week sits in a quiet list
// underneath with no position next to their name. "Eighth of eight" is not
// information somebody can act on, and it is the most reliable way to make a
// person stop opening an app. The manager still sees exactly who they are, and
// the screen reads as a list of people to go and talk to.
//
// The weights are on the screen. A points system nobody can explain is
// arbitrary, and arbitrary is demotivating. Every rule says why it is worth
// what it is worth.
//
// It is the manager's screen. A public ranking of VOLUNTARY behaviour gets
// gamed in a week: practice is the one thing here nobody is made to do, which
// is what makes it honest, and it stops being honest the moment somebody can
// climb a table with forty junk drills. A salesperson sees their own week on
// their own dashboard, without the ranking.

import { useEffect, useState } from 'react'
import { Box, HStack, VStack, Text, Spinner, Badge, Collapse, Button } from '@chakra-ui/react'
import { Trophy, ChevronDown, ChevronUp } from 'lucide-react'
import { displayNameFor } from '../../lib/names'
import { SCORING, buildBoard, boardHeadline, WEEK_DAYS } from '../../lib/floorScore'
import { loadFloorWeek } from '../../lib/floorWeek'

function Line({ row }) {
  return (
    <HStack spacing={2} flexWrap="wrap" rowGap={1}>
      {row.lines.map((l) => (
        <Text key={l.key} fontSize="sm" color="inkMuted" fontFamily="mono" whiteSpace="nowrap">
          {l.counted}
          {l.capped ? ` of ${l.raw}` : ''} {l.label.toLowerCase()}
        </Text>
      ))}
    </HStack>
  )
}

export default function FloorBoard({ members }) {
  const [counts, setCounts] = useState(null)
  const [error, setError] = useState(null)
  const [showRules, setShowRules] = useState(false)

  useEffect(() => {
    let live = true
    loadFloorWeek().then(({ counts: c, error: e }) => {
      if (!live) return
      setCounts(c)
      setError(e)
    })
    return () => {
      live = false
    }
  }, [])

  if (!counts) {
    return (
      <Box>
        <Spinner size="sm" />
      </Box>
    )
  }

  const { scored, quiet } = buildBoard(members, counts)
  const headline = boardHeadline({ scored, quiet })

  return (
    <Box>
      <HStack align="baseline" justify="space-between" mb={0.5} flexWrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Trophy size={16} aria-hidden="true" />
          <Text fontSize="lg" fontWeight={600}>
            Putting the work in
          </Text>
        </HStack>
        <Text fontSize="sm" color="inkDim" fontFamily="mono">
          last {WEEK_DAYS} days
        </Text>
      </HStack>
      <Text fontSize="body" color="inkMuted" mb={3} maxW="measure" lineHeight={1.55}>
        {headline} This counts process, not deliveries, which is what Jazz asks a manager to
        recognise. Nothing here is a selling number.
      </Text>

      {error && (
        <Text fontSize="sm" color="warn" mb={2}>
          Some of this week could not be read, so the totals may be low.
        </Text>
      )}

      {scored.length > 0 && (
        <VStack align="stretch" spacing={0} mb={quiet.length ? 5 : 0}>
          {scored.map((row) => (
            <HStack
              key={row.person.id}
              py={3}
              borderTop="1px solid"
              borderColor="line"
              justify="space-between"
              align="flex-start"
              spacing={4}
            >
              <HStack spacing={3} align="flex-start" minW={0}>
                <Text
                  fontFamily="mono"
                  fontSize="sm"
                  color={row.rank === 1 ? 'oxblood' : 'inkDim'}
                  fontWeight={row.rank === 1 ? 700 : 500}
                  pt="2px"
                  minW="1.5rem"
                >
                  {row.rank}
                </Text>
                <Box minW={0}>
                  <Text fontWeight={600} fontSize="body">
                    {displayNameFor(row.person) || 'Somebody'}
                  </Text>
                  <Line row={row} />
                </Box>
              </HStack>
              <Text fontFamily="mono" fontSize="lg" fontWeight={600} whiteSpace="nowrap">
                {row.total}
              </Text>
            </HStack>
          ))}
        </VStack>
      )}

      {quiet.length > 0 && (
        <Box>
          <Text fontSize="sm" fontWeight={600} color="inkMuted" mb={1}>
            Nothing recorded this week
          </Text>
          <Text fontSize="sm" color="inkDim" mb={2} maxW="measure" lineHeight={1.55}>
            No position against their name on purpose. This is who to go and talk to, not a bottom
            of a table.
          </Text>
          <HStack spacing={2} flexWrap="wrap" rowGap={2}>
            {quiet.map(({ person }) => (
              <Badge key={person.id} variant="subtle" fontWeight={500} fontSize="xs" px={2} py={1}>
                {displayNameFor(person) || 'Somebody'}
              </Badge>
            ))}
          </HStack>
        </Box>
      )}

      <Button
        variant="ghost"
        size="xs"
        mt={4}
        onClick={() => setShowRules((v) => !v)}
        rightIcon={showRules ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      >
        How this is counted
      </Button>
      <Collapse in={showRules} animateOpacity>
        <VStack align="stretch" spacing={0} mt={2}>
          {SCORING.map((s) => (
            <HStack key={s.key} py={2.5} borderTop="1px solid" borderColor="line" align="flex-start" spacing={4}>
              <Text fontFamily="mono" fontSize="sm" fontWeight={600} minW="2.5rem" pt="1px">
                {s.points}
              </Text>
              <Box>
                <Text fontSize="body" fontWeight={600}>
                  {s.label}
                  {s.cap ? (
                    <Text as="span" fontSize="sm" color="inkDim" fontFamily="mono" ml={2}>
                      max {s.cap} a week
                    </Text>
                  ) : null}
                </Text>
                <Text fontSize="sm" color="inkMuted" lineHeight={1.55} maxW="measure">
                  {s.why}
                </Text>
              </Box>
            </HStack>
          ))}
        </VStack>
      </Collapse>
    </Box>
  )
}
